import React from 'react';
import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import { CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

import { Badge } from '@oysterjs/ui/badge';
import { Description, Field, Label } from '@oysterjs/ui/fieldset';
import { SegmentedControl, SegmentedControlOption } from '@oysterjs/ui/segmented-control';

import { type TemplatePickerOption } from '~/components/cara-input/cara-input-template-picker-dialog';
import { type RunCardTemplate, type RunCardTemplateKind } from './run-card-submission';
import { type UseCaseSpec } from './use-case-catalog';

const KIND_LABEL: Record<RunCardTemplateKind, string> = {
  word: 'Word',
  html: 'Doc',
  excel: 'Excel',
  customForm: 'Form'
};

export const RunCardDeliverable = ({
  spec,
  selectedKey,
  onSelectKey,
  libraries,
  template,
  onSelectTemplate
}: {
  spec: UseCaseSpec;
  selectedKey?: string;
  onSelectKey: (key: string) => void;
  libraries: Partial<Record<RunCardTemplateKind, TemplatePickerOption[]>>;
  template?: RunCardTemplate;
  onSelectTemplate: (template: RunCardTemplate) => void;
}) => {
  const [query, setQuery] = React.useState('');

  const templatesFor = React.useCallback(
    (kinds?: RunCardTemplateKind[]) =>
      ((kinds ?? (Object.keys(KIND_LABEL) as RunCardTemplateKind[])) as RunCardTemplateKind[])
        .flatMap((kind) => (libraries[kind] ?? []).map((t) => ({ id: t.id, title: t.title, kind })))
        .sort((a, b) => a.title.localeCompare(b.title)),
    [libraries]
  );

  const deliverable = spec.deliverable;

  // Hide empty template segments unless that is the only choice — otherwise the
  // submit button cites a picker that is not on screen.
  const withTemplates = (deliverable?.choices ?? []).filter(
    (choice) => !choice.template || templatesFor(choice.kinds).length > 0
  );
  const choices = withTemplates.length > 0 ? withTemplates : (deliverable?.choices ?? []);
  const selected = choices.find((choice) => choice.key === selectedKey) ?? choices[0];

  const templates = selected?.template ? templatesFor(selected.kinds) : [];
  const matches = templates.filter((t) =>
    t.title.toLowerCase().includes(query.trim().toLowerCase())
  );

  if (!deliverable || choices.length === 0) {
    return null;
  }

  const showSegments = choices.length > 1;

  return (
    <Field>
      <Label>{deliverable.label}</Label>
      <Description>{deliverable.description}</Description>
      {showSegments && (
        <>
          <SegmentedControl
            value={selected?.key}
            onChange={onSelectKey}
            aria-label={deliverable.label}
            className="mt-2"
          >
            {choices.map((choice) => (
              <SegmentedControlOption key={choice.key} value={choice.key} icon={choice.icon}>
                {choice.label}
              </SegmentedControlOption>
            ))}
          </SegmentedControl>
          {/* The selected option's own consequence, under the control that set it.
              A segment label can say "Excel workbook" and cannot say that the
              write-up still arrives, which is the thing people assumed away. */}
          {selected && (
            <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
              {selected.description}
            </p>
          )}
        </>
      )}
      {selected?.template && (
        <div
          data-slot="control"
          className="mt-3 overflow-hidden rounded-lg border border-neutral-950/10 dark:border-white/10 shadow-sm"
        >
          {/* A bare Headless input: the kit's `Input` carries its own border,
              background and focus ring, and this field sits inside a box that
              already draws them. */}
          <div className="flex items-center gap-2 border-b border-neutral-950/10 dark:border-white/10 px-3">
            <MagnifyingGlassIcon className="size-4 shrink-0 text-neutral-500 dark:text-neutral-400" />
            <Headless.Input
              name="template-search"
              aria-label="Search templates"
              placeholder="Search templates"
              value={query}
              onChange={(e) => setQuery(e.currentTarget.value)}
              className="w-full border-0 bg-transparent py-2 text-sm/6 text-neutral-950 dark:text-white placeholder:text-neutral-400 dark:placeholder:text-neutral-500 focus:outline-none focus:ring-0"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1.5">
            {matches.length === 0 && (
              <p className="px-3 py-2 text-sm text-neutral-500 dark:text-neutral-400">
                {templates.length === 0
                  ? 'Nothing in this library yet.'
                  : `No templates match “${query}”.`}
              </p>
            )}
            {matches.map((option) => {
              const isSelected = template?.id === option.id && template.kind === option.kind;
              return (
                <Headless.Button
                  key={`${option.kind}-${option.id}`}
                  type="button"
                  onClick={() => onSelectTemplate(option)}
                  className={clsx(
                    'flex w-full items-center gap-2 px-3 py-1.5 text-left',
                    isSelected
                      ? 'bg-primary-50 dark:bg-primary-500/10'
                      : 'hover:bg-neutral-50 dark:hover:bg-white/5'
                  )}
                >
                  <span
                    className={clsx(
                      'min-w-0 flex-1 truncate text-sm/6',
                      isSelected
                        ? 'text-primary-900 dark:text-primary-200'
                        : 'text-neutral-950 dark:text-white'
                    )}
                  >
                    {option.title}
                  </span>
                  <Badge color="zinc">{KIND_LABEL[option.kind]}</Badge>
                  <CheckIcon
                    className={clsx(
                      'size-4 shrink-0',
                      isSelected ? 'text-primary-600 dark:text-primary-400' : 'text-transparent'
                    )}
                  />
                </Headless.Button>
              );
            })}
          </div>
        </div>
      )}
    </Field>
  );
};
